import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useCollections } from '../components/knowledge/hooks/useCollections'
import { useContents } from '../components/knowledge/hooks/useContents'
import CollectionsSidebar from '../components/knowledge/CollectionsSidebar'
import ContentsPanel from '../components/knowledge/ContentsPanel'
import KnowledgeStats from '../components/knowledge/KnowledgeStats'
import { KnowledgeOnboardingGuide } from '../components/onboarding'
import type { ContentStatus, KnowledgeFilters } from '../components/knowledge/types'
import { DEFAULT_FILTERS } from '../components/knowledge/types'

/**
 * Page Base de Connaissances
 * Fusion des pages Subjects et Media en une interface unifiée
 */
export default function KnowledgesPage() {
  // État de la sélection
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | 'orphan' | null>(null)
  const [filters, setFilters] = useState<KnowledgeFilters>(DEFAULT_FILTERS)
  const [statsOpen, setStatsOpen] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)

  // Hooks de données
  const { 
    collections, 
    loading: collectionsLoading, 
    error: collectionsError,
    create: createCollection,
    rename: renameCollection,
    remove: removeCollection,
  } = useCollections()

  const { 
    contents, 
    allContents,
    loading: contentsLoading, 
    error: contentsError,
    reload: reloadContents,
    remove: removeContent,
    reindex,
    forceTranscribe,
    getDetail,
    downloadTranscription,
    updateCollection,
    workingIds,
    statusCounts,
    orphanCount,
  } = useContents({ 
    collectionId: selectedCollectionId,
    status: filters.status,
    enablePolling: true,
  })

  // Compteurs de contenus par collection (sur TOUS les contenus)
  const contentCountByCollection = useMemo(() => {
    const counts: Record<number, number> = {}
    for (const content of allContents) {
      if (content.subject_id) {
        counts[content.subject_id] = (counts[content.subject_id] || 0) + 1
      }
    }
    return counts
  }, [allContents])

  // Total des contenus (sur TOUS les contenus)
  const totalContents = allContents.length

  // Collection sélectionnée
  const selectedCollection = useMemo(() => {
    if (selectedCollectionId === 'orphan' || selectedCollectionId === null) return null
    return collections.find(c => c.id === selectedCollectionId) || null
  }, [collections, selectedCollectionId])

  // Titre du panel droit
  const panelTitle = useMemo(() => {
    if (selectedCollectionId === 'orphan') return 'Contenus sans collection'
    if (selectedCollection) return `Contenus de "${selectedCollection.title}"`
    return 'Tous les contenus'
  }, [selectedCollectionId, selectedCollection])

  const loading = collectionsLoading || contentsLoading
  const error = collectionsError || contentsError

  return (
    <div className="relative overflow-hidden min-h-screen">
      {/* Background */}
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[360px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_65%)]" />
      <div aria-hidden className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_25%_18%,rgba(236,72,153,0.14),transparent_60%),radial-gradient(circle_at_80%_12%,rgba(168,85,247,0.18),transparent_55%)] dark:bg-[radial-gradient(circle_at_20%_18%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_85%_18%,rgba(168,85,247,0.15),transparent_50%)]" />

      <div className="relative mx-auto max-w-[1600px] px-6 py-6">
        {/* Barre d'actions */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Gérez vos collections et contenus pour enrichir vos assistants IA
          </p>
          <div className="flex items-center gap-3">
            {/* Bouton toggle sidebar mobile */}
            <button
              type="button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden inline-flex items-center gap-2 rounded-full border border-zinc-200/70 bg-white/80 px-3 py-2 text-sm font-semibold text-zinc-600 shadow-sm transition hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              Collections
            </button>
            <button
              type="button"
              onClick={() => setStatsOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-50/80 px-4 py-2 text-sm font-semibold text-emerald-700 shadow-sm transition hover:bg-emerald-100 dark:border-emerald-900/60 dark:bg-emerald-900/30 dark:text-emerald-200 dark:hover:bg-emerald-900/50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="hidden sm:inline">Statistiques</span>
            </button>
            <Link
              to="/knowledges/sync"
              className="inline-flex items-center gap-2 rounded-full border border-blue-200/70 bg-blue-50/80 px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-100 dark:border-blue-900/60 dark:bg-blue-900/30 dark:text-blue-200 dark:hover:bg-blue-900/50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="hidden sm:inline">Synchronisations</span>
            </Link>
          </div>
        </div>

        {/* Erreur globale */}
        {error && (
          <div className="mb-6 rounded-2xl border border-red-200/70 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </div>
        )}

        {/* Layout principal */}
        <div className="flex gap-6">
          {/* Sidebar Collections - Desktop */}
          <div className="hidden lg:block">
            <CollectionsSidebar
              collections={collections}
              loading={collectionsLoading}
              selectedId={selectedCollectionId}
              onSelect={setSelectedCollectionId}
              totalContents={totalContents}
              orphanCount={orphanCount}
              contentCountByCollection={contentCountByCollection}
              onCreate={async (title) => {
                const success = await createCollection({ title })
                return success
              }}
              onRename={renameCollection}
              onDelete={removeCollection}
            />
          </div>

          {/* Sidebar Collections - Mobile/Tablet (drawer) */}
          {sidebarOpen && (
            <div className="lg:hidden fixed inset-0 z-40">
              <div 
                className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
                onClick={() => setSidebarOpen(false)} 
              />
              <div className="absolute inset-y-0 left-0 w-80 max-w-[85vw] bg-white/95 shadow-2xl dark:bg-zinc-950/95 animate-slide-in-left">
                <div className="flex items-center justify-between border-b border-zinc-200/70 px-4 py-3 dark:border-zinc-800">
                  <h2 className="font-semibold text-zinc-900 dark:text-white">Collections</h2>
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="rounded-full p-1.5 text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="p-4">
                  <CollectionsSidebar
                    collections={collections}
                    loading={collectionsLoading}
                    selectedId={selectedCollectionId}
                    onSelect={(id) => {
                      setSelectedCollectionId(id)
                      setSidebarOpen(false)
                    }}
                    totalContents={totalContents}
                    orphanCount={orphanCount}
                    contentCountByCollection={contentCountByCollection}
                    onCreate={async (title) => {
                      const success = await createCollection({ title })
                      return success
                    }}
                    onRename={renameCollection}
                    onDelete={removeCollection}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Panel Contenus */}
          <ContentsPanel
            contents={contents}
            collections={collections}
            loading={contentsLoading}
            panelTitle={panelTitle}
            selectedCollectionId={selectedCollectionId}
            workingIds={workingIds}
            statusFilter={filters.status}
            onStatusFilterChange={(status) => setFilters(f => ({ ...f, status }))}
            onReload={reloadContents}
            onRemove={removeContent}
            onReindex={reindex}
            onForceTranscribe={forceTranscribe}
            onGetDetail={getDetail}
            onDownloadTranscription={downloadTranscription}
            onUpdateCollection={updateCollection}
            uploadOpen={uploadOpen}
            onUploadOpenChange={setUploadOpen}
          />
        </div>
      </div>

      {/* Modal Stats */}
      <KnowledgeStats
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        collections={collections}
        statusCounts={statusCounts}
        totalContents={totalContents}
        orphanCount={orphanCount}
      />

      {/* Onboarding Guide */}
      <KnowledgeOnboardingGuide
        uploadPanelOpen={uploadOpen}
      />
    </div>
  )
}
